import React, { useState, useEffect } from 'react';
import { RefreshCcw, ChevronDown, Filter } from 'lucide-react';
import MultiSelectDropdownInline from '../multi-select-inline';
import { FilterState } from './types';

interface JobsiteVisitsFiltersProps {
  activeFilters: FilterState;
  setActiveFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  provinces: { id: string; name: string }[];
  statusOptions: { id: string; name: string }[];
  unionStatusOptions: { id: string; name: string }[];
  organizers: { id: string; name: string; role: { id: string; roleName: string } }[];
  jobsites?: { id: string; name: string }[];
  localUnions?: { id: string; name: string }[];
  handleApplyFilters: () => void;
  handleFilterClearAll: () => void;
  isJobsiteListByOrganizerFiltersValid: () => boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const JobsiteVisitsFilters: React.FC<JobsiteVisitsFiltersProps> = ({
  activeFilters,
  setActiveFilters,
  provinces,
  statusOptions,
  unionStatusOptions,
  organizers,
  jobsites = [],
  localUnions = [],
  handleApplyFilters,
  handleFilterClearAll,
  isJobsiteListByOrganizerFiltersValid,
  setLoading
}) => {
  // Local state for pending filter changes (not applied yet)
  const [pendingFilters, setPendingFilters] = useState<FilterState>(activeFilters);

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      (activeFilters.startDate && activeFilters.startDate !== '') ||
      (activeFilters.endDate && activeFilters.endDate !== '') ||
      (activeFilters.organizer && activeFilters.organizer.length > 0) ||
      (activeFilters.province && activeFilters.province.length > 0) ||
      (activeFilters.unionStatus && activeFilters.unionStatus.length > 0) ||
      (activeFilters.localUnion && activeFilters.localUnion.length > 0) ||
      (activeFilters.jobsite && activeFilters.jobsite.length > 0)
    );
  };

  // State for collapsible filter section - expand if filters are applied
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(hasActiveFilters());

  // Sync pending filters when active filters change externally (e.g., when cleared)
  useEffect(() => {
    setPendingFilters(activeFilters);
    // Auto-expand when filters are applied, collapse when cleared
    setIsFiltersExpanded(hasActiveFilters());
  }, [activeFilters]);

  // Handle apply - sync pending filters to active filters
  const handleApplyClick = () => {
    setActiveFilters(pendingFilters);
    handleApplyFilters();
  };

  // Handle clear - reset both pending and active filters
  const handleClearClick = () => {
    const emptyFilters: FilterState = {
      sector: [],
      province: [],
      status: [],
      startDate: '',
      endDate: '',
      search: '',
      organizer: [],
      unionStatus: [],
      jobsite: [],
      localUnion: []
    };
    setPendingFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setLoading(true);
    handleFilterClearAll();
  };
  return (
    <div className="bg-white rounded-[12px] shadow mb-4">
      {/* Filter Toggle Button */}
      <div className="p-3 sm:p-4 border-b border-gray-200">
        <button
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          id="toggle-filters-button"
        >
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filters</span>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isFiltersExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Collapsible Filter Content */}
      {isFiltersExpanded && (
        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 items-end">
        {/* Start Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Start Date</label>
          <input
            id="organizer-report-start-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 md:w-[95%]"
            value={pendingFilters.startDate ? pendingFilters.startDate.split('T')[0] : ''}
            max={pendingFilters.endDate ? pendingFilters.endDate.split('T')[0] : undefined}
            onChange={(e) => {
              setPendingFilters(prev => ({
                ...prev,
                startDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* End Date */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">End Date</label>
          <input
            id="organizer-report-end-date-input"
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:h-10 full md:w-[95%]"
            value={pendingFilters.endDate ? pendingFilters.endDate.split('T')[0] : ''}
            min={pendingFilters.startDate ? pendingFilters.startDate.split('T')[0] : undefined}
            onChange={(e) => {
              setPendingFilters(prev => ({
                ...prev,
                endDate: e.target.value ? new Date(e.target.value).toISOString() : ''
              }));
            }}
          />
        </div>

        {/* Organizer Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Organizer Name</label>
          <div id="organizer-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="organizer-dropdown"
              options={organizers}
              onSelect={(selectedIds: string[]) => {
                setPendingFilters(prev => ({
                  ...prev,
                  organizer: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={pendingFilters.organizer || []}
              placeholder="Select organizers"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Province Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Province</label>
          <div id="organizer-report-province-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="organizer-report-province-dropdown"
              options={provinces}
              onSelect={(selectedIds: string[]) => {
                setPendingFilters(prev => ({
                  ...prev,
                  province: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={pendingFilters.province}
              placeholder="Select provinces"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Union Status Section */}
        <div className="w-full">
          <label className="block text-sm text-gray-600 mb-1">Union Status</label>
          <div id="monthly-union-status-dropdown-container" className="relative">
            <MultiSelectDropdownInline
              testId="monthly-union-status-dropdown"
              options={unionStatusOptions}
              onSelect={(selectedIds: string[]) => {
                setPendingFilters(prev => ({
                  ...prev,
                  unionStatus: selectedIds
                }));
              }}
              getValue={(option: { id: string; name: string }) => option.id}
              getDisplayText={(option: { id: string; name: string }) => option.name}
              selectedValues={pendingFilters.unionStatus || []}
              placeholder="Select union status"
              dropdownContainerClassName="dropdown-append"
            />
          </div>
        </div>

        {/* Local Unions Section */}
        {localUnions.length > 0 && (
          <div className="w-full">
            <label className="block text-sm text-gray-600 mb-1">Local Union</label>
            <div id="local-unions-dropdown-container" className="relative">
              <MultiSelectDropdownInline
                testId="local-unions-dropdown"
                options={localUnions}
                onSelect={(selectedIds: string[]) => {
                  setPendingFilters(prev => ({
                    ...prev,
                    localUnion: selectedIds
                  }));
                }}
                getValue={(option: { id: string; name: string }) => option.id}
                getDisplayText={(option: { id: string; name: string }) => option.name}
                selectedValues={pendingFilters.localUnion || []}
                placeholder="Select local unions"
                dropdownContainerClassName="dropdown-append"
              />
            </div>
          </div>
        )}

        {/* Jobsite Section */}
        {jobsites.length > 0 && (
          <div className="w-full">
            <label className="block text-sm text-gray-600 mb-1">Jobsite</label>
            <div id="jobsite-dropdown-container" className="relative">
              <MultiSelectDropdownInline
                testId="jobsite-dropdown"
                options={jobsites}
                onSelect={(selectedIds: string[]) => {
                  setPendingFilters(prev => ({
                    ...prev,
                    jobsite: selectedIds
                  }));
                }}
                getValue={(option: { id: string; name: string }) => option.id}
                getDisplayText={(option: { id: string; name: string }) => option.name}
                selectedValues={pendingFilters.jobsite || []}
                placeholder="Select jobsites"
                dropdownContainerClassName="dropdown-append"
              />
            </div>
          </div>
        )}

        {/* Apply Button */}
        <div className="w-full sm:col-span-2 lg:col-span-3 xl:grid-cols-3 flex justify-end mt-4 gap-2">
          <button
            id="organizer-report-apply-filters-button"
            className={`btn-primary whitespace-nowrap flex-1 sm:flex-none ${!isJobsiteListByOrganizerFiltersValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleApplyClick}
            disabled={!isJobsiteListByOrganizerFiltersValid()}
          >
            Apply Filters
          </button>
          <button
            id="organizer-report-refresh-button"
            className="btn-secondary whitespace-nowrap"
            onClick={handleClearClick}
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default JobsiteVisitsFilters;
